/**
 * Returns the next 90° clockwise rotation, cycling 0 → 90 → 180 → 270 → 0.
 *
 * @param {number} angle - the current rotation in degrees (0, 90, 180 or 270)
 * @returns {number} the next rotation angle
 */
export const getNextRotation = angle => (angle + 90) % 360

/**
 * Builds the CSS `transform` value for a rotated scan video.
 *
 * At 0° no transform is applied. At 180° the rotation alone is enough — the
 * bounding box is unchanged. At 90°/270° the video's width and height swap,
 * so it is also scaled down by the shorter/longer side ratio to fit inside
 * the (unchanged) landscape frame without clipping or overflowing.
 *
 * @param {number} angle - the rotation in degrees: 0, 90, 180 or 270
 * @param {number} width - the rendered video frame width, in pixels
 * @param {number} height - the rendered video frame height, in pixels
 * @returns {string} a CSS `transform` value
 */
export const getRotationTransform = (angle, width, height) => {
  if (angle === 0) {
    return 'none'
  }

  if (angle === 180) {
    return 'rotate(180deg)'
  }

  // 90° / 270°: width and height swap, so scale down to fit the original frame.
  if (width > 0 && height > 0) {
    const ratio = Math.min(width, height) / Math.max(width, height)
    const scale = Math.round(ratio * 10000) / 10000
    return `rotate(${angle}deg) scale(${scale})`
  }

  return `rotate(${angle}deg)`
}
